import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChefHat,
  Heart,
  Plane,
  Car,
  Brush,
  PartyPopper,
  Clock,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import PhoneAnchor from "./PhoneAnchor";

export const SERVICE_GRID = [
  { key: "private_chef",    icon: ChefHat,      name: "Private Chef",       tag: "Signature",   blurb: "In-home dining, booked from the guidebook." },
  { key: "massage",         icon: Heart,        name: "In-Home Massage",    tag: "Signature",   blurb: "Wellness arrivals and couples' treatments." },
  { key: "airport_ride",    icon: Plane,        name: "Airport Transfer",   tag: "Signature",   blurb: "On-demand rides from wheels-down." },
  { key: "arrival_event",   icon: PartyPopper,  name: "Event Setup",        tag: "Signature",   blurb: "Birthdays, proposals, anniversaries." },
  { key: "mid_stay_cleaning", icon: Brush,      name: "Mid-Stay Cleaning",  tag: "Signature",   blurb: "Fresh linens and resets on longer stays." },
  { key: "car_rental",      icon: Car,          name: "Car Rentals",        tag: "Signature",   blurb: "Pre-booked and delivered to the door." },
  { key: "early_checkin",   icon: Clock,        name: "Early Check-In",     tag: "Earnings",    blurb: "Charge $25–$100+ for flexible arrivals." },
  { key: "late_checkout",   icon: Clock,        name: "Late Check-Out",     tag: "Earnings",    blurb: "Monetize the gap between bookings." },
  { key: "other",           icon: Sparkles,     name: "Custom Requests",    tag: "Signature",   blurb: "Guests ask, your team delivers." },
];

const KEY_TO_DEMO_ID = {
  private_chef: "private-chef",
  massage: "massage",
  airport_ride: "airport-pickup",
  arrival_event: "arrival-event",
  wellness_therapy: "wellness-therapy",
  restaurant_reservations: "restaurant-reservations",
  other: "other",
};

export default function StageServices({
  previewToggles,
  onToggle,
  hostLogo,
}) {
  const enabledServices = React.useMemo(() => {
    const set = new Set(["grocery-delivery"]);
    Object.entries(previewToggles || {}).forEach(([k, on]) => {
      if (on) {
        const id = KEY_TO_DEMO_ID[k];
        if (id) set.add(id);
      }
    });
    return set;
  }, [previewToggles]);

  const enabledCount = Object.values(previewToggles || {}).filter(Boolean).length;

  return (
    <div className="relative w-full h-full grid grid-rows-[minmax(0,42%)_minmax(0,58%)] laptop:grid-rows-1 laptop:grid-cols-[1.25fr_0.75fr]">
      {/* Left — selector */}
      <div className="relative px-6 laptop:px-14 py-3 laptop:py-6 order-2 laptop:order-1 min-h-0 flex flex-col overflow-hidden">
        <div className="mb-3 laptop:mb-5 flex items-end justify-between gap-4">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888] mb-1.5">
              Step 2 · Preview
            </p>
            <h2 className="text-[22px] laptop:text-[28px] font-extrabold text-[#111111] leading-tight" style={{ letterSpacing: -0.4 }}>
              Tap a service to see what guests see.
            </h2>
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={enabledCount}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="hidden tabletOnly:flex laptop:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#EAEAEA]"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#EF4949]" />
              <span className="text-[11px] font-semibold text-[#111111] tabular-nums">
                {enabledCount} {enabledCount === 1 ? "service" : "services"} on
              </span>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Desktop/tablet — stacked grid with controlled overflow */}
        <div className="hidden tabletOnly:block laptop:block flex-1 min-h-0">
          <div className="grid grid-cols-2 desktop:grid-cols-3 gap-2 laptop:gap-2.5 h-full overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>
            {SERVICE_GRID.map((svc) => (
              <ServiceChip
                key={svc.key}
                svc={svc}
                on={!!previewToggles?.[svc.key]}
                onClick={() => onToggle(svc.key, !previewToggles?.[svc.key])}
                variant="full"
              />
            ))}
          </div>
        </div>

        {/* Mobile — swipeable snap rail of compact chips */}
        <div className="tabletOnly:hidden laptop:hidden flex-1 min-h-0 overflow-hidden">
          <div
            className="h-full overflow-x-auto overflow-y-hidden flex gap-2 snap-x snap-mandatory -mx-6 px-6 pb-2"
            style={{ scrollbarWidth: "none" }}
          >
            {SERVICE_GRID.map((svc) => (
              <div key={svc.key} className="snap-start flex-shrink-0 w-[78%]">
                <ServiceChip
                  svc={svc}
                  on={!!previewToggles?.[svc.key]}
                  onClick={() => onToggle(svc.key, !previewToggles?.[svc.key])}
                  variant="compact"
                />
              </div>
            ))}
          </div>
        </div>

        <p className="mt-2 laptop:mt-3 text-[11px] text-[#888888]">
          <span className="hidden laptop:inline">Everything updates in the guest preview — toggle to see the story shift.</span>
          <span className="laptop:hidden">Swipe and tap — guests see the changes instantly.</span>
        </p>
      </div>

      {/* Right — phone */}
      <div className="relative order-1 laptop:order-2 min-h-0 overflow-hidden min-h-[38dvh] laptop:min-h-0">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(500px 400px at 55% 45%, rgba(239,73,73,0.06), transparent 60%)",
          }}
        />
        <div className="relative h-full flex items-center justify-center py-2 laptop:py-6">
          <PhoneAnchor
            stage={2}
            enabledServices={enabledServices}
            hostLogo={hostLogo}
          />
        </div>
      </div>
    </div>
  );
}

function ServiceChip({ svc, on, onClick, variant = "full" }) {
  const Icon = svc.icon;
  const isEarnings = svc.tag === "Earnings";
  return (
    <motion.button
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.985 }}
      onClick={onClick}
      className={`group w-full text-left relative overflow-hidden rounded-[14px] border transition-all ${
        on
          ? "bg-white border-[#FECACA] shadow-[0_10px_30px_rgba(0,0,0,0.05)]"
          : "bg-[#F7F7F8] border-[#EAEAEA] hover:bg-white"
      }`}
    >
      <div className="flex items-start gap-2.5 p-2.5 laptop:p-3">
        <div
          className={`w-9 h-9 rounded-[10px] flex-shrink-0 flex items-center justify-center transition-colors ${
            on
              ? isEarnings
                ? "bg-[#111111] text-white"
                : "bg-[#EF4949] text-white"
              : "bg-white text-[#888888] border border-[#EAEAEA]"
          }`}
        >
          <Icon size={15} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[13px] font-bold text-[#111111] truncate">{svc.name}</span>
            {isEarnings && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-[6px] bg-[#F7F7F8] border border-[#EAEAEA] text-[8px] font-bold uppercase tracking-[0.12em] text-[#5F6368]">
                <TrendingUp size={8} strokeWidth={2.5} />
                Earns
              </span>
            )}
          </div>
          {variant === "full" ? (
            <p className="text-[11px] text-[#5F6368] leading-snug mt-0.5 line-clamp-2">
              {svc.blurb}
            </p>
          ) : (
            <p className="text-[11px] text-[#5F6368] leading-snug mt-0.5 line-clamp-1">
              {svc.blurb}
            </p>
          )}
        </div>
        <div
          className={`flex-shrink-0 mt-1 w-4 h-4 rounded-full flex items-center justify-center transition-colors ${
            on ? "bg-[#EF4949]" : "border border-[#EAEAEA] bg-white"
          }`}
        >
          {on && (
            <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
              <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
      </div>
    </motion.button>
  );
}
